import { BASE_URL } from 'config';
import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from "react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import { Box, Typography, CircularProgress } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import axios from "axios";
import GiftSection from "./GiftSection";
import { useGetProductsByStoreQuery } from "../../redux/Slices/offersApi";

const PurchaseRewardSection = forwardRef((props, ref) => {
  const { storeId, error, giftTypes = [] } = props;
  const [amount, setAmount] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const giftSectionRef = useRef();
  const [didLoadFromApi, setDidLoadFromApi] = useState(false);

  // RTK Query for products (filtered by store if selected)
  const { data: productsData, isLoading: productsLoading } = useGetProductsByStoreQuery(storeId);
  const products = productsData?.data || [];

  // Clear selected products when store changes
  useEffect(() => {
    setSelectedProducts([]);
  }, [storeId]);

  // جلب الفئات
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        const token = localStorage.getItem("user-token");
        const response = await axios.get(
          BASE_URL + "/administrator/categories",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response.data.status && Array.isArray(response.data.data)) {
          setCategories(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, []);

  useImperativeHandle(ref, () => ({
    getPurchaseRewardData: () => {
      const purchaseRewards = [];

      // إضافة مكافأة الشراء الأساسية
      purchaseRewards.push({
        amount: amount,
        ...(selectedProducts.length > 0 && { product_id: selectedProducts[0].id }),
        ...(selectedCategories.length > 0 && { category_id: selectedCategories[0].id }),
      });

      return {
        purchase_rewards: purchaseRewards,
      };
    },
    getGiftData: () => {
      return giftSectionRef.current
        ? giftSectionRef.current.getGiftData()
        : {
          rewards: {
            promotion_gift_types: [],
            free_products: [],
          },
        };
    },
    reset: () => {
      setAmount("");
      setSelectedProducts([]);
      setSelectedCategories([]);
      if (giftSectionRef.current) {
        giftSectionRef.current.reset();
      }
    },
    setPurchaseRewardDataFromApi: (purchaseRewards, giftTypes, freeProducts) => {
      if (didLoadFromApi) return;
      if (purchaseRewards && purchaseRewards.length > 0) {
        setAmount(purchaseRewards[0].amount || "");
        setSelectedProducts(
          purchaseRewards[0].product_id
            ? products.filter((p) => p.id === purchaseRewards[0].product_id)
            : []
        );
        setSelectedCategories(
          purchaseRewards[0].category_id
            ? categories.filter((c) => c.id === purchaseRewards[0].category_id)
            : []
        );
      }
      if (giftSectionRef.current && giftTypes) {
        giftSectionRef.current.setGiftDataFromApi(giftTypes, freeProducts);
      }
      setDidLoadFromApi(true);
    },
  }));

  useEffect(() => {
    setDidLoadFromApi(false);
  }, [window.location.pathname]);

  return (
    <MDBox
      display="flex"
      flexDirection="column"
      gap={3}
      p={3}
      boxShadow={3}
      borderRadius="16px"
      bgcolor="#fff"
      mb={4}
      sx={{ width: "90%", margin: "20px auto" }}
    >
      <MDTypography variant="h6" fontWeight="bold" mb={0}>
        بيانات مكافأة الشراء
      </MDTypography>
      <MDBox display="flex" gap={2} flexDirection={{ xs: "column", md: "row" }} width="100%">
        <Autocomplete
          multiple
          options={products}
          loading={productsLoading}
          getOptionLabel={(option) => option.name || ""}
          value={selectedProducts}
          onChange={(_, newValue) => setSelectedProducts(newValue)}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderOption={(props, option) => (
            <Box
              component="li"
              {...props}
              key={option.id}
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <Box
                component="img"
                src={option.medias && option.medias.length > 0 ? option.medias[0].file_name : ""}
                alt={option.name}
                sx={{ width: 32, height: 32, objectFit: "cover", borderRadius: 1 }}
              />
              <Typography variant="body2">{option.name}</Typography>
            </Box>
          )}
          renderInput={(params) => (
            <MDInput
              {...params}
              label="اختر المنتجات"
              fullWidth
              inputProps={{ ...params.inputProps, dir: "rtl" }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {productsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              sx={{ height: 45 }}
            />
          )}
          sx={{ flex: 1 }}
        />
        <Autocomplete
          multiple
          options={categories}
          loading={categoriesLoading}
          getOptionLabel={(option) => option.name || ""}
          value={selectedCategories}
          onChange={(_, newValue) => setSelectedCategories(newValue)}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderOption={(props, option) => (
            <Box
              component="li"
              {...props}
              key={option.id}
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <Box
                component="img"
                src={
                  option.image
                    ? `https://back.al-balad.sa/uploads/categories/images/${option.image}`
                    : ""
                }
                alt={option.name}
                sx={{ width: 32, height: 32, objectFit: "cover", borderRadius: 1 }}
              />
              <Typography variant="body2">{option.name}</Typography>
            </Box>
          )}
          renderInput={(params) => (
            <MDInput
              {...params}
              label="اختر الفئات"
              fullWidth
              inputProps={{ ...params.inputProps, dir: "rtl" }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {categoriesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              sx={{ height: 45 }}
            />
          )}
          sx={{ flex: 1 }}
        />
        <MDInput
          label="المبلغ"
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          sx={{ flex: 1, height: 45 }}
          inputProps={{ dir: "rtl" }}
          required
        />
      </MDBox>
      {error && (
        <Typography color="error" fontSize={13}>
          {error.message || error}
        </Typography>
      )}
      <GiftSection ref={giftSectionRef} error={props.rewardsError} giftTypes={giftTypes} />
    </MDBox>
  );
});

export default PurchaseRewardSection;
